import {minutes, mapValues, get, flow, camelCase, upperFirst, size, groupBy, sortBy, pick, createSelector} from 'helpers';
import {createdDateNegative} from 'store/common';
import {tags} from 'store/tags';
import {sorting} from 'store/sorting';
import {activities} from 'store';
import {goals} from 'store/goals/selectors/goals';

export const sort = createSelector(
  [ tags, sorting, goals ],
  ( tags, sorting, goals ) => (activities, preference) => {
    // activities
    const { Completed, Missed, NotStarted, OnHold } = groupBy(activities, byStatus);

    switch (preference) {
      case 'priority': {
        const sorted = sortBy(NotStarted, byDate, time, createdDateNegative);
        const groups = pick(groupBy(sorted, byPriority), 'Highest', 'High', 'Normal');
        return { ...groups, Missed, Completed, OnHold };
      }

      case 'goal': {
        const getGoal = activity => get(goals[activity.goal_id], 'name');
        const sorted = sortBy(NotStarted, getGoal, byDate, time, createdDateNegative);
        const { noGoal, ...groups } = groupBy(sorted, flow(getGoal, goal => goal || 'noGoal'));
        return { ...groups, '': noGoal, Missed, Completed, OnHold };
      }

      case 'tags': {
        const { withoutTag, ...tagGroups } = tags.sort(NotStarted);
        return {
          ...mapValues(tagGroups, group => sortBy(group, byDate, time, createdDateNegative)),
          withoutTag: sortBy(withoutTag, byDate, time, createdDateNegative),
          Missed,
          Completed,
          OnHold,
        };
      }

      case 'customOrder': {
        const sorted = sortBy(NotStarted, activity => get(sorting, activity.id, -1));
        return { '': sorted, Missed, Completed, OnHold };
      }

      case 'date':
      case 'dateReverse': {
        const sorted = sortBy(NotStarted, preference == 'date' ? byDate : byDateReverse, time);
        const { Someday, hasDate } = groupBy(sorted, activity => activity.ts ? 'hasDate' : 'Someday');
        return preference == 'date' ? { '': hasDate, Someday, Missed, Completed, OnHold } : { Someday, '': hasDate, Missed, Completed, OnHold };
      }

      //by time
      default: {
        const sorted = sortBy(NotStarted, time, createdDateNegative);
        const groups = groupBy(sorted, byTime);
        const ordered = pick(groups, 'Morning', 'Afternoon', 'Evening', 'All day', 'Any time');
        if (size(ordered) > 1) {
          return { ...ordered, Missed, Completed, OnHold };
        }
        return { '': sorted, Missed, Completed, OnHold };
      }
    }
  },
);

function byStatus({ status }) {
  return upperFirst(camelCase(status));
}

function byTime(activity) {
  if (activities.allDay(activity)) {
    return 'All day';
  }
  return activities.dayParts(activity) || 'Any time';
}

function byDate(activity) {
  if (!activity.repeatType) {
    return -1*(activity.ts || 9999999999);
  }
}

function byDateReverse(activity) {
  return activity.ts;
}

function byPriority(activity) {
  return activity.priority || 'Normal';
}

function time({ time }) {
  return minutes(time);
}
